import React from "react";
import { Tooltip } from "@mui/material";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import GreenToggle from "../GreenToggle";
import { brandRgba } from "../../helpers/brandColor";

/**
 * SettingRow / SettingSection — the white, icon-chip + toggle list style
 * originally built for Feed Settings (src/pages/Feed/FeedPage.jsx), extracted
 * here so other settings screens (e.g. TV View) can reuse the same look
 * instead of re-implementing it.
 */

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 11v5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

// A single toggleable setting row — compact single line: icon + title
// (+ optional info tooltip for the "why") and a toggle switch. `brand` is
// passed in as a prop instead of closed over, so identity stays stable
// across renders regardless of where it's rendered.
export const SettingRow = ({ icon, title, subtitle, checked, onChange, brand, disabled = false }) => (
  <SoftBox
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap="0.75rem"
    sx={{ padding: "0.625rem 0" }}
  >
    <SoftBox display="flex" alignItems="center" gap="10px" sx={{ minWidth: 0 }}>
      <SoftBox
        sx={{
          width: "26px",
          height: "26px",
          borderRadius: "7px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          background: brandRgba(brand, 0.1),
          color: brand,
        }}
      >
        {icon}
      </SoftBox>
      <SoftTypography
        sx={{
          fontSize: "14px!important",
          fontWeight: "600!important",
          color: "#1D2939",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </SoftTypography>
      {subtitle && (
        <Tooltip
          title={subtitle}
          arrow
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={2500}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#FFFFFF",
                color: "#344054",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: 1.4,
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #E4E7EC",
                boxShadow: "0px 8px 16px rgba(16,24,40,0.12)",
              },
            },
            arrow: {
              sx: { color: "#FFFFFF", "&::before": { border: "1px solid #E4E7EC" } },
            },
          }}
        >
          <SoftBox
            component="span"
            role="img"
            aria-label={subtitle}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              color: "#8A94A6",
              cursor: "help",
              flexShrink: 0,
              transition: "background 120ms ease, color 120ms ease",
              "&:hover": { background: brandRgba(brand, 0.1), color: brand },
            }}
          >
            <InfoIcon />
          </SoftBox>
        </Tooltip>
      )}
    </SoftBox>
    <GreenToggle checked={checked} onChange={onChange} disabled={disabled} size="sm" ariaLabel={title} />
  </SoftBox>
);

// A flat group of setting rows under a bold, color-accented section label —
// a colored bar + tinted icon act as the "anchor" the eye jumps to, with
// hairline dividers between rows instead of nesting a bordered card per group.
export const SettingSection = ({ title, icon, brand, children }) => (
  <SoftBox>
    <SoftBox display="flex" alignItems="center" gap="8px" sx={{ marginBottom: "0.5rem" }}>
      <SoftBox sx={{ width: "3px", height: "14px", borderRadius: "2px", background: brand, flexShrink: 0 }} />
      {icon && <SoftBox sx={{ color: brand, display: "flex" }}>{icon}</SoftBox>}
      <SoftTypography
        sx={{
          fontSize: "13.5px!important",
          fontWeight: "800!important",
          color: "#0B1220",
          textTransform: "uppercase",
          letterSpacing: "0.045em",
        }}
      >
        {title}
      </SoftTypography>
    </SoftBox>
    <SoftBox sx={{ "& > *:not(:last-child)": { borderBottom: "1px solid #EDEFF2" } }}>
      {children}
    </SoftBox>
  </SoftBox>
);
